import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useCompany, toTelHref, toWhatsAppHref } from "@/lib/useCompany";
import { useSiteContent } from "@/lib/useSiteContent";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/context";

const FALLBACK_NAV = [
  { to: "/", label: "Startseite" },
  { to: "/reparatur", label: "Reparatur" },
  { to: "/preise", label: "Preise" },
  { to: "/termin-buchen", label: "Termin buchen" },
  { to: "/reparaturstatus", label: "Reparaturstatus" },
  { to: "/versand-reparatur", label: "Versand-Reparatur" },
  { to: "/standorte", label: "Standorte" },
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dynamicNav, setDynamicNav] = useState(null);
  const loc = useLocation();
  const company = useCompany();
  const site = useSiteContent();
  const { lang, t } = useI18n();

  useEffect(() => {
    const load = () => {
      api.get("/nav-menu")
        .then((r) => setDynamicNav(r.data.map(n => ({ to: n.href, label: n.label, external: n.open_in_new_tab }))))
        .catch(() => setDynamicNav(null));
    };
    load();
    const onUpd = () => load();
    window.addEventListener("handymeister:data-updated", onUpd);
    window.addEventListener("focus", onUpd);
    return () => {
      window.removeEventListener("handymeister:data-updated", onUpd);
      window.removeEventListener("focus", onUpd);
    };
  }, [loc.pathname]);

  const translatedFallback = [
    { to: "/", label: t("nav.home") }, { to: "/reparatur", label: t("nav.repair") },
    { to: "/preise", label: t("nav.prices") }, { to: "/termin-buchen", label: t("nav.appointment") },
    { to: "/reparaturstatus", label: t("nav.status") }, { to: "/versand-reparatur", label: t("nav.shipping") },
    { to: "/standorte", label: t("nav.locations") }, { to: "/ueber-uns", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") }, { to: "/kontakt", label: t("nav.contact") },
  ];
  const activeDynamicNav = dynamicNav && dynamicNav.length ? [...dynamicNav] : [...FALLBACK_NAV];
  if (!activeDynamicNav.some((item) => item.to === "/reparaturstatus")) {
    activeDynamicNav.splice(4, 0, FALLBACK_NAV.find((item) => item.to === "/reparaturstatus"));
  }
  const nav = ["en", "ar"].includes(lang) ? translatedFallback : activeDynamicNav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const logoText = company.company_name || "Handy Meister";
  // Support two-word logos where the second word gets the accent color.
  const parts = logoText.split(" ");
  const first = parts[0];
  const rest = parts.slice(1).join(" ");

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 glass-header ${scrolled ? "shadow-[0_10px_30px_rgba(0,0,0,0.25)]" : ""} transition-[box-shadow,border-color] duration-300`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 lg:h-18 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
              {company.logo_url && company.logo_url.trim() && company.logo_url !== "https://example.com/logo.png" ? (
                <img src={company.logo_url} alt={logoText} className="h-8 w-auto object-contain" />
              ) : (
                <span className="font-display text-xl font-semibold tracking-tight">
                  {first} {rest && <span className="text-[#0055FF]">{rest}</span>}
                </span>
              )}
            </Link>

            <nav className="hidden xl:flex items-center gap-0.5" aria-label="Hauptnavigation">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `px-2.5 py-2 rounded-full text-[13px] font-medium ${
                      isActive
                        ? "text-[#0055FF] bg-[#0055FF]/8"
                        : "text-zinc-300 hover:text-white hover:bg-white/[0.06]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <a
                href={toTelHref(company.phone)}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-zinc-300 hover:bg-white/[0.06]"
                data-testid="header-phone"
              >
                <Phone size={15} /> <span className="tabular-nums">{company.phone}</span>
              </a>
              <a
                href={toWhatsAppHref(company.whatsapp)}
                target="_blank" rel="noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-emerald-400 hover:bg-emerald-500/15"
                data-testid="header-whatsapp"
              >
                <MessageCircle size={15} /> {site.header_whatsapp_label || "WhatsApp"}
              </a>
              <LanguageSwitcher />
              <Link
                to="/reparatur"
                className="btn-primary hidden sm:inline-flex min-h-11 items-center gap-1"
                data-testid="header-start-repair-btn"
              >
                {site.header_start_repair_btn || "Reparatur starten"} <ChevronRight size={16} />
              </Link>
              <button
                className="xl:hidden inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/[0.06]"
                onClick={() => setOpen(!open)}
                aria-label="Menü"
                data-testid="mobile-menu-toggle"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-black/5 bg-[#131316]" data-testid="mobile-menu">
            <div className="px-4 py-3 flex flex-col">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mnav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `py-3 px-2 text-[15px] rounded-lg ${
                      isActive ? "text-[#0055FF] bg-[#0055FF]/6" : "text-zinc-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/reparatur"
                className="btn-primary mt-2 justify-center inline-flex items-center gap-1"
                data-testid="mobile-start-repair-btn"
              >
                {site.header_start_repair_btn || "Reparatur starten"} <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
